import { AxiosResponse } from "axios";
import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import { isHash } from "../../samples_cloud/utils";
import { ValidateQueryResponse } from "../@types/validatequery";
import api from "../api";
import { extractNetworkValuesFromQuery, tryCatch, wrapInQuotes } from "../util";
import { isQueryEncoded } from "./utils";
import { isNetworkOnlySearch } from "../components/network/utils";
import {
  LOCATION,
  NETWORK_SEARCH_LIMIT,
  NETWORK_SEARCH_LIMIT_ERROR_MESSAGE
} from "../consts";

export type ValidateQuery = {
  mutate: (
    query: string,
    location?: string
  ) => Promise<AxiosResponse<ValidateQueryResponse>>;
  data: ValidateQueryResponse | undefined;
  isLoading: boolean;
  shouldSwitchBulkSearchToSummary: (hashes: Array<string>) => Promise<boolean>;
  abort: () => void;
  error: unknown | undefined;
};

const validateNetworkQuery = (
  response: AxiosResponse<ValidateQueryResponse>
) => {
  const { networkValues, tooLongNetworkValues } = extractNetworkValuesFromQuery(
    response.data?.transformed_query ?? ""
  );
  if (
    networkValues.length + tooLongNetworkValues.length >
    NETWORK_SEARCH_LIMIT
  ) {
    return {
      ...response,
      data: {
        ...response.data,
        is_valid: false,
        err_message: NETWORK_SEARCH_LIMIT_ERROR_MESSAGE
      }
    } as AxiosResponse<ValidateQueryResponse>;
  }

  return response;
};

const useValidateQuery = (initialIsLoading = true): ValidateQuery => {
  const [isLoading, setIsLoading] = useState(initialIsLoading);
  const abortControllerRef = useRef<AbortController | null>(null);

  const abortValidation = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const validateQuery = async ({
    query,
    location
  }: {
    query: string;
    location?: string;
  }) => {
    // abort previous request if there is any
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const isEncoded = isQueryEncoded(query);

    if (isEncoded) {
      show({
        message:
          "URL encoded string has been detected in query. Query will be automatically decoded and sent to search.",
        level: NotificationsLevel.INFO
      });
    }

    const quotedQuery = wrapInQuotes(query);
    const decodedQuery = decodeURIComponent(quotedQuery);

    const newLocation = isNetworkOnlySearch(decodedQuery)
      ? LOCATION.NETWORK
      : location;

    return validateNetworkQuery(
      await api.validateQuery(decodedQuery, newLocation, abortController.signal)
    );
  };

  const { mutateAsync, data, error } = useMutation({
    mutationKey: ["validateQuery"],
    mutationFn: validateQuery,
    onMutate: () => {
      setIsLoading(true);
    }
  });

  const shouldSwitchBulkSearchToSummary = async (hashes: Array<string>) => {
    if (hashes.length !== 1) return false;
    const [hash] = hashes;
    if (!isHash(hash)) return false;
    const [validationData] = await tryCatch(mutateAsync({ query: hash }));

    const { data: { is_only_hash_or_search } = {} } = (validationData ||
      {}) as AxiosResponse<ValidateQueryResponse>;

    return Boolean(is_only_hash_or_search);
  };

  const mutate = (query: string, location?: string) =>
    mutateAsync({ query, location }).finally(() => {
      setIsLoading(false);
    });

  return {
    mutate,
    data: data?.data,
    error,
    isLoading,
    shouldSwitchBulkSearchToSummary,
    abort: abortValidation
  };
};

export default useValidateQuery;
